import { useSettings } from "./useSettings";
import { QuoteTemplatesSection } from "../SettingsQuoteTemplates";
import { QuoteTermsSection } from "../SettingsQuoteTerms";

export default function SettingsQuotes() {
  const { s, setS, save } = useSettings();
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      <section id="set-quote-numbering" className="card-flat space-y-3 p-6" data-testid="quote-numbering-settings">
        <p className="label-caps">Quote numbering &amp; defaults</p>
        <p className="text-sm text-muted-foreground">Service-quote numbers follow {"{prefix}-{year}-{seq}"} (blank prefix = Q). Valid days pre-fills new quotes; rate per acre powers the pricing helper.</p>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Prefix</span>
          <input data-testid="quote-number-prefix" className="field-input flex-1" placeholder="Q" maxLength={8} value={s.quote_number_prefix ?? ""}
            onChange={(e) => setS({ ...s, quote_number_prefix: e.target.value })} />
          <span className="text-sm font-semibold">Valid for</span>
          <input data-testid="quote-valid-days" type="number" min="1" max="180" className="field-input w-20" value={s.quote_default_valid_days ?? 30}
            onChange={(e) => setS({ ...s, quote_default_valid_days: Number(e.target.value) })} />
          <span className="text-sm font-semibold">days</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">$</span>
          <input data-testid="quote-rate-per-acre" type="number" min="0" className="field-input w-28" value={s.quote_rate_per_acre ?? 0}
            onChange={(e) => setS({ ...s, quote_rate_per_acre: Number(e.target.value) })} />
          <span className="text-sm font-semibold">rate per acre (pricing helper suggestion)</span>
        </div>
        <button data-testid="quote-numbering-save" onClick={() => save({ quote_number_prefix: s.quote_number_prefix ?? "", quote_default_valid_days: Number(s.quote_default_valid_days ?? 30), quote_rate_per_acre: Number(s.quote_rate_per_acre ?? 0) })}
          className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save quote defaults</button>
      </section>

      <section id="set-quote-permissions" className="card-flat space-y-3 p-6" data-testid="quote-permissions-settings">
        <p className="label-caps">Quote permissions</p>
        <label className="flex items-center justify-between gap-3 rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Dispatchers can send service quotes<span className="block text-xs font-normal text-muted-foreground">Off = dispatchers can draft quotes but only you can send them</span></span>
          <button data-testid="dispatcher-can-send-quotes" onClick={() => save({ dispatcher_can_send_quotes: !s.dispatcher_can_send_quotes })}
            className={`h-8 w-14 shrink-0 rounded-full transition-colors ${s.dispatcher_can_send_quotes ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${s.dispatcher_can_send_quotes ? "translate-x-7" : ""}`} />
          </button>
        </label>
      </section>

      <section id="set-quote-nudges" className="card-flat space-y-3 p-6" data-testid="quote-nudge-settings">
        <p className="label-caps">Quote follow-up nudges</p>
        <p className="text-sm text-muted-foreground">Automatically email owners a friendly nudge when a project quote sits undecided — undecided quotes are the biggest leak in your funnel.</p>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Nudge undecided quotes</span>
          <button data-testid="quote-nudge-toggle" onClick={() => save({ quote_reminder_enabled: !(s.quote_reminder_enabled ?? true) })}
            className={`h-8 w-14 rounded-full transition-colors ${(s.quote_reminder_enabled ?? true) ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${(s.quote_reminder_enabled ?? true) ? "translate-x-7" : ""}`} />
          </button>
        </label>
        {(s.quote_reminder_enabled ?? true) && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Nudge after</span>
            <input data-testid="quote-nudge-days" type="number" min="1" className="field-input w-20" value={s.quote_reminder_after_days ?? 3} onFocus={(e) => e.target.select()}
              onChange={(e) => setS({ ...s, quote_reminder_after_days: Number(e.target.value) })} />
            <span>days undecided (repeats on the same cycle)</span>
            <button data-testid="quote-nudge-save" onClick={() => save({ quote_reminder_after_days: Number(s.quote_reminder_after_days ?? 3) })}
              className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
          </div>
        )}
      </section>

      <section id="set-surcharge" className="card-flat space-y-3 p-6" data-testid="surcharge-settings">
        <p className="label-caps">One-time job surcharge</p>
        <p className="text-sm text-muted-foreground">Ad-hoc work at properties without a seasonal agreement usually carries a 50–80% surcharge locally. Shown as a hint when you quote add-ons.</p>
        <div className="flex items-center gap-2">
          <input data-testid="surcharge-pct" type="number" className="field-input w-28" value={s.one_time_surcharge_pct} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, one_time_surcharge_pct: Number(e.target.value) })} />
          <span className="text-sm font-semibold">%</span>
          <button data-testid="surcharge-save" onClick={() => save({ one_time_surcharge_pct: Number(s.one_time_surcharge_pct) })} className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground">Save</button>
        </div>
      </section>

      <div id="set-quote-templates"><QuoteTemplatesSection /></div>
      <div id="set-quote-terms"><QuoteTermsSection /></div>
    </div>
  );
}
